import { useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { Check, Play, RefreshCw, TriangleAlert } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";
import { exportHref } from "@jbnl/api-client";
import { AlertList } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DataTable,
  DataTableSkeleton,
  dataTableFeatures,
} from "@/components/ui/data-table";
import type { Filterdescriptor } from "@/components/ui/data-table-filter";
import { ListDetail } from "@/components/ListDetail";
import { datumTijd } from "@/i18n/datum";
import { type Verplaatsing } from "./CelDialog";
import { zonderVoorvoegsel } from "./naam";
import { Speelschemagrid } from "./Speelschemagrid";
import { datum, tijd } from "./tijd";
import {
  alleDefinitief,
  useDefinitiefMaken,
  useHerbereken,
  usePlanning,
  useTerugzetten,
  type PlanningDag,
  type PlanningNiveau,
} from "./usePlanning";

const helper = createColumnHelper<typeof dataTableFeatures, PlanningDag>();

/** Sorts on the ISO value, not the `dd-MM-yyyy` text, which puts 01-06 before 16-05. */
const opDatum = (a: { original: PlanningDag }, b: { original: PlanningDag }) =>
  a.original.datum.localeCompare(b.original.datum);

const kolommen = (t: TFunction<"planning">, definitief: ReadonlySet<string>) =>
  helper.columns([
    helper.accessor((dag) => datum(dag.datum), {
      id: "datum",
      header: t("lijst.datum"),
      sortFn: opDatum,
      // Nowrap for the reason the wedstrijddagenlijst gives: the list pane is the narrow half of
      // the screen, and a date broken over two lines reads as two dates.
      cell: (info) => (
        <span className="font-medium whitespace-nowrap">{info.getValue()}</span>
      ),
    }),
    // Each niveau has its own status (ADR-0081), so the list says which dagen are already fixed.
    helper.accessor((dag) => dag.niveauNaam, {
      id: "niveau",
      header: t("lijst.niveau"),
      cell: (info) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {info.getValue()}
          {definitief.has(info.row.original.niveauId) && (
            <Badge>{t("stand.definitief")}</Badge>
          )}
        </span>
      ),
    }),
    // Zonder voorvoegsel, per `naam.ts`: a column of "Jeugdbrandweer <plaats>" is a column of
    // plaatsen with the same word in front of each.
    //
    // Cut in the accessor rather than in the cell, so the column sorts and searches on what it
    // shows. That does mean the whole naam pasted out of a mail no longer finds its row - which
    // is the trade this makes on purpose: "Jeugdbrandweer" as a search term matched every row of
    // the season, and the plaats is what anyone types.
    helper.accessor(
      (dag) =>
        dag.organiserendKorpsNaam
          ? zonderVoorvoegsel(dag.organiserendKorpsNaam)
          : t("lijst.nogNietBekend"),
      {
        id: "korps",
        header: t("lijst.korps"),
        cell: (info) =>
          info.row.original.organiserendKorpsNaam ? (
            info.getValue()
          ) : (
            <span className="text-muted-foreground">
              {t("lijst.nogNietBekend")}
            </span>
          ),
      },
    ),
    // ADR-0038 requires the gap to be visible where a beheerder is scanning a season, not only on
    // the dag they happen to open. This column is that requirement.
    helper.accessor((dag) => dag.waarschuwingen.length, {
      id: "waarschuwingen",
      header: t("lijst.waarschuwingen"),
      cell: (info) =>
        info.getValue() > 0 ? (
          <Badge variant="destructive">{info.getValue()}</Badge>
        ) : (
          <span className="text-muted-foreground">{t("lijst.geen")}</span>
        ),
    }),
  ]);

/** §4.4's planning for one seizoen, with what the planner could not do above both panes. */
export function Planning({
  seizoenId,
  dagId,
  onKies,
}: {
  seizoenId: string;
  dagId: string | undefined;
  onKies: (id?: string) => void;
}) {
  const { t } = useTranslation("planning");

  const {
    data: planning,
    isPending,
    isFetching,
    refetch,
    error,
  } = usePlanning(seizoenId);

  const dag = planning?.dagen.find((d) => d.id === dagId);

  // A move being under way lives here rather than in the grid, so that picking up a ploeg on the
  // 27th and putting it down on the 10th is one gesture: the grid remounts when the chosen dag
  // changes, and this does not.
  const [verplaatsing, setVerplaatsing] = useState<Verplaatsing | null>(null);

  const definitieveNiveaus = useMemo(
    () =>
      new Set(
        planning?.niveaus.filter((n) => n.isDefinitief).map((n) => n.niveauId),
      ),
    [planning],
  );

  const columns = kolommen(t, definitieveNiveaus);

  // Held whole and local (#194): datum and korps already have the search box above and a season
  // is short enough to scan, so waarschuwingen - the column a beheerder actually triages by - is
  // the one filter this list earns.
  const descriptors = useMemo<Filterdescriptor<PlanningDag>[]>(
    () => [
      {
        id: "waarschuwingen",
        label: t("lijst.filterWaarschuwingen"),
        kind: "schakelaar",
        past: (dag) => dag.waarschuwingen.length > 0,
      },
    ],
    [t],
  );

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("fout.title")}</CardTitle>
          <CardDescription>{error.message}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // The engine runs out of band after every write that could move the planning, so a seizoen
  // whose first run has not landed yet is an ordinary state rather than an error (ADR-0038).
  if (!isPending && planning && planning.planningId === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("nogGeen.title")}</CardTitle>
          <CardDescription>{t("nogGeen.description")}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <PlanningKop
        planning={planning}
        bezig={isFetching}
        onVernieuw={() => void refetch()}
      />

      <ListDetail
        selected={Boolean(dag)}
        backLabel={t("lijst.terug")}
        onBack={() => onKies()}
        list={
          <Card>
            <CardContent className="pt-6">
              {isPending ? (
                <DataTableSkeleton
                  label={t("lijst.label")}
                  columns={columns}
                  rows={6}
                  search
                />
              ) : (
                <DataTable
                  label={t("lijst.label")}
                  data={planning?.dagen ?? []}
                  columns={columns}
                  getRowId={(row) => row.id}
                  selectedId={dag?.id}
                  onSelect={onKies}
                  searchPlaceholder={t("lijst.zoek")}
                  empty={t("lijst.leeg")}
                  exportHref={exportHref(
                    "/api/beheer/seizoenen/{seizoenId}/planning/export",
                    { path: { seizoenId } },
                  )}
                  clientPageSize={25}
                  filters={{ descriptors }}
                />
              )}
            </CardContent>
          </Card>
        }
        detail={
          <PlanningDetail
            seizoenId={seizoenId}
            planning={planning}
            dag={dag}
            verplaatsing={verplaatsing}
            onVerplaatsing={setVerplaatsing}
            t={t}
          />
        }
      />
    </>
  );
}

/**
 * Everything above both panes: the toolbar shows once a planning exists at all, and the two
 * warning blocks only when there is something in them to warn about.
 */
function PlanningKop({
  planning,
  bezig,
  onVernieuw,
}: {
  planning: NonNullable<ReturnType<typeof usePlanning>["data"]> | undefined;
  bezig: boolean;
  onVernieuw: () => void;
}) {
  if (!planning) {
    return null;
  }

  return (
    <>
      <Stand planning={planning} bezig={bezig} onVernieuw={onVernieuw} />
      {planning.ongeplaatst.length > 0 && <Ongeplaatst planning={planning} />}
      {planning.verspreid.length > 0 && <Verspreid planning={planning} />}
    </>
  );
}

/** The detail pane: the grid once a dag is chosen, otherwise a nudge to pick one from the list. */
function PlanningDetail({
  seizoenId,
  planning,
  dag,
  verplaatsing,
  onVerplaatsing,
  t,
}: {
  seizoenId: string;
  planning: NonNullable<ReturnType<typeof usePlanning>["data"]> | undefined;
  dag: PlanningDag | undefined;
  verplaatsing: Verplaatsing | null;
  onVerplaatsing: (verplaatsing: Verplaatsing | null) => void;
  t: TFunction<"planning">;
}) {
  if (!dag || !planning) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">{t("lijst.kies")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Speelschemagrid
      key={dag.id}
      seizoenId={seizoenId}
      dagen={planning.dagen}
      dag={dag}
      ongeplaatst={planning.ongeplaatst}
      verplaatsing={verplaatsing}
      onVerplaatsing={onVerplaatsing}
    />
  );
}

/**
 * The planner runs out of band after every write that could move the planning (ADR-0038), so this
 * is a snapshot. Herberekenen disables on `gevraagdSinds` rather than `draait`, which the job has
 * not necessarily set by the time the click lands - five clicks each queued their own run.
 */
function Stand({
  planning,
  bezig,
  onVernieuw,
}: {
  planning: NonNullable<ReturnType<typeof usePlanning>["data"]>;
  bezig: boolean;
  onVernieuw: () => void;
}) {
  const { t } = useTranslation(["planning", "common"]);
  const herbereken = useHerbereken(planning.seizoenId);

  // Derived rather than cleared by an effect: the moment `laatstBerekendOp` moves off this
  // snapshot, the run this button asked for either landed or something else's did, and
  // `wachtOpBevestiging` reads false on its own from then on - nothing has to reset it, which is
  // also why a stray click that leaves it stuck comparing against a value nothing will ever
  // produce again is harmless rather than a leak.
  const [gevraagdSinds, setGevraagdSinds] = useState<
    string | null | undefined
  >();
  const backstop = useRef<number | undefined>(undefined);

  const wachtOpBevestiging =
    gevraagdSinds !== undefined && planning.laatstBerekendOp === gevraagdSinds;

  const draait = planning.draait || wachtOpBevestiging;
  const fout = eersteFout(herbereken.error);
  const allesDefinitief = alleDefinitief(planning);

  return (
    <div className="mb-4 space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-muted-foreground">
          {laatstBerekendTekst(t, planning.laatstBerekendOp)}
        </span>

        {draait && (
          <span className="text-muted-foreground">{t("stand.draait")}</span>
        )}

        {fout && <span className="text-destructive">{fout.message}</span>}

        <div className="ms-auto flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onVernieuw}
            disabled={bezig}
          >
            <RefreshCw aria-hidden />
            {t("stand.vernieuwen")}
          </Button>

          {/* Disabled rather than hidden: §4.4.6 stops the engine, and a vanished button reads as broken. */}
          <Button
            variant="outline"
            size="sm"
            disabled={allesDefinitief || draait || herbereken.isPending}
            title={herberekenTitel(t, allesDefinitief)}
            onClick={() => {
              // Set synchronously, in the same click, rather than from the mutation's onSuccess:
              // `herbereken.isPending` alone only covers the POST itself, which answers in
              // milliseconds - nowhere near long enough to stop a second click before the first
              // has even reached the job queue. The button is disabled the instant this runs, so
              // there is at most one of these pending at a time - clearing first is only to be
              // sure an earlier click's backstop cannot outlive it.
              window.clearTimeout(backstop.current);
              setGevraagdSinds(planning.laatstBerekendOp);
              backstop.current = window.setTimeout(
                () => setGevraagdSinds(undefined), // skipcq: JS-W1042 - a state setter needs its argument
                15_000,
              );

              herbereken.mutate(undefined, {
                // Refused (a now-definitieve planning) or failed outright: nothing was queued, so
                // there is nothing for `laatstBerekendOp` to ever move past.
                onError: () => {
                  setGevraagdSinds(undefined); // skipcq: JS-W1042 - a state setter needs its argument
                  window.clearTimeout(backstop.current);
                },
              });
            }}
          >
            <Play aria-hidden />
            {actieLabel(
              draait || herbereken.isPending,
              t("stand.herbereken"),
              t("common:actions.busy"),
            )}
          </Button>
        </div>
      </div>

      {planning.niveaus.length > 0 && (
        <ul
          aria-label={t("niveaus.label")}
          className="divide-y rounded-md border bg-card px-3"
        >
          {planning.niveaus.map((niveau) => (
            <NiveauStand
              key={niveau.niveauId}
              seizoenId={planning.seizoenId}
              niveau={niveau}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** One niveau's status with its own button: definitief is decided per niveau (ADR-0081). */
function NiveauStand({
  seizoenId,
  niveau,
}: {
  seizoenId: string;
  niveau: PlanningNiveau;
}) {
  const { t } = useTranslation(["planning", "common"]);
  const definitiefMaken = useDefinitiefMaken(seizoenId, niveau.niveauId);
  const terugzetten = useTerugzetten(seizoenId, niveau.niveauId);
  const fout = eersteFout(definitiefMaken.error, terugzetten.error);

  return (
    <li className="flex flex-wrap items-center gap-3 py-2">
      <span className="font-medium">{niveau.niveauNaam}</span>
      <StandBadge isDefinitief={niveau.isDefinitief} t={t} />

      {niveau.definitiefOp && (
        <span className="text-muted-foreground">
          {niveau.definitiefDoor
            ? t("niveaus.definitiefOpDoor", {
                moment: datumTijd(niveau.definitiefOp),
                naam: niveau.definitiefDoor,
              })
            : t("niveaus.definitiefOp", {
                moment: datumTijd(niveau.definitiefOp),
              })}
        </span>
      )}

      {fout && <span className="text-destructive">{fout.message}</span>}

      <div className="ms-auto">
        {/* One control, not two: offering both would ask which state the screen thinks it is in. No
           confirmation - this is undone by the button that replaces it. */}
        {niveau.isDefinitief ? (
          <TerugzetKnop
            terugzetten={terugzetten}
            niveau={niveau.niveauNaam}
            t={t}
          />
        ) : (
          <DefinitiefMakenKnop
            definitiefMaken={definitiefMaken}
            niveau={niveau.niveauNaam}
            t={t}
          />
        )}
      </div>
    </li>
  );
}

function actieLabel(bezig: boolean, label: string, bezigLabel: string) {
  return bezig ? bezigLabel : label;
}

function laatstBerekendTekst(
  t: TFunction<"planning">,
  laatstBerekendOp: string | null,
) {
  return laatstBerekendOp
    ? t("stand.laatstBerekend", { moment: datumTijd(laatstBerekendOp) })
    : t("stand.nooitBerekend");
}

// Every niveau being definitief is the only reason this button is ever disabled with a title on it.
function herberekenTitel(t: TFunction<"planning">, allesDefinitief: boolean) {
  return allesDefinitief ? t("stand.definitiefTitel") : undefined;
}

function eersteFout(...fouten: Array<Error | null | undefined>) {
  return fouten.find((fout) => fout != null);
}

/**
 * §4.4.6: the boekje is out and the ploegen have been told where to be, so the planner does not
 * run again. Handmatig overrulen blijft wel kunnen - dat is §4.4.4's altijd.
 */
function StandBadge({
  isDefinitief,
  t,
}: {
  isDefinitief: boolean;
  t: TFunction<"planning">;
}) {
  return isDefinitief ? (
    <Badge>{t("stand.definitief")}</Badge>
  ) : (
    <Badge variant="muted">{t("stand.concept")}</Badge>
  );
}

function TerugzetKnop({
  terugzetten,
  niveau,
  t,
}: {
  terugzetten: ReturnType<typeof useTerugzetten>;
  niveau: string;
  t: TFunction<["planning", "common"]>;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={terugzetten.isPending}
      aria-label={t("stand.terugzettenVoor", { niveau })}
      onClick={() => terugzetten.mutate()}
    >
      {actieLabel(
        terugzetten.isPending,
        t("stand.terugzetten"),
        t("common:actions.busy"),
      )}
    </Button>
  );
}

/* A question first: this is the moment korpsen and ondersteuners start reading the planning. */
function DefinitiefMakenKnop({
  definitiefMaken,
  niveau,
  t,
}: {
  definitiefMaken: ReturnType<typeof useDefinitiefMaken>;
  niveau: string;
  t: TFunction<["planning", "common"]>;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          disabled={definitiefMaken.isPending}
          aria-label={t("stand.definitiefMakenVoor", { niveau })}
        >
          <Check aria-hidden />
          {actieLabel(
            definitiefMaken.isPending,
            t("stand.definitiefMaken"),
            t("common:actions.busy"),
          )}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("stand.bevestig.titel", { niveau })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("stand.bevestig.uitleg", { niveau })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common:actions.cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={() => definitiefMaken.mutate()}>
            {t("stand.bevestig.bevestigen")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * The korpsen that asked for one wedstrijddag and did not get it (ADR-0052). Warning rather than
 * destructive, and no remedy line: a samenvoorkeur loses to a voorkeursdatum by design.
 *
 * Grouped by category rather than one bullet per korps (#152): the server groups by how many
 * wedstrijddagen a korps landed on, so only a shared outcome nests names under one sentence.
 */
function Verspreid({
  planning,
}: {
  planning: NonNullable<ReturnType<typeof usePlanning>["data"]>;
}) {
  const { t } = useTranslation("planning");

  const aantalKorpsen = planning.verspreid.reduce(
    (totaal, categorie) => totaal + categorie.korpsen.length,
    0,
  );

  return (
    <AlertList
      tone="warning"
      className="mb-6"
      title={t("verspreid.title", { count: aantalKorpsen })}
      description={t("verspreid.description")}
    >
      {/* The sentences are the server's; a second copy in TypeScript is the one that drifts. */}
      <ul className="space-y-2">
        {planning.verspreid.map((categorie) => (
          <li key={`${categorie.code}-${categorie.wedstrijddagenCount}`}>
            {categorie.tekst}
            {/* A group of one already names its korps in the sentence above. */}
            {categorie.korpsen.length > 1 && (
              <ul className="list-disc space-y-1 pl-5">
                {categorie.korpsen.map((korps) => (
                  <li key={korps.korpsId}>
                    {zonderVoorvoegsel(korps.korpsNaam)}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </AlertList>
  );
}

/**
 * Above the panes rather than in the detail: a ploeg is ongeplaatst on no dag, so there is none
 * to put it under. ADR-0038 is explicit that this is told loudly.
 */
function Ongeplaatst({
  planning,
}: {
  planning: NonNullable<ReturnType<typeof usePlanning>["data"]>;
}) {
  const { t } = useTranslation("planning");

  return (
    // A red field and not a red edge: a ploeg with nowhere to play is the one thing on this
    // screen a beheerder has to resolve before the season runs, and a white card with a thin
    // border reads as another panel among panels.
    <Card className="mb-6 border-destructive-border bg-destructive-surface">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TriangleAlert className="size-4 text-destructive" aria-hidden />
          {t("ongeplaatst.title", { count: planning.ongeplaatst.length })}
        </CardTitle>
        <CardDescription className="text-destructive-muted">
          {t("ongeplaatst.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2 text-sm">
          {planning.ongeplaatst.map((ploeg) => (
            <li key={`${ploeg.ploegId}-${ploeg.klasseId}`}>
              <span className="font-medium">
                {zonderVoorvoegsel(ploeg.ploegNaam)}
              </span>
              {/* Not `muted-foreground`: see the token's own note in index.css - grey-500
                  clears AA on white by a hair and fails on the red field under it. */}
              <span className="text-destructive-muted">
                {" "}
                ·{" "}
                {t("ongeplaatst.regel", {
                  korps: zonderVoorvoegsel(ploeg.korpsNaam),
                  klasse: ploeg.klasseNaam,
                })}
              </span>
              <p className="text-destructive-muted">{ploeg.reden}</p>

              {/* Where that sentence is about (#121). A reden states a rule over a set - every
                  baan of this klasse, every dag that was left - and without the set a beheerder
                  reading it has no way back to the grid. */}
              {ploeg.plekken.length > 0 && (
                <>
                  <p className="text-destructive-muted">
                    {t("ongeplaatst.plekkenTitel")}
                  </p>
                  <ul className="list-disc pl-5 text-destructive-muted">
                    {ploeg.plekken.map((plek) => {
                      // Named the way the wedstrijddagenlijst names a dag, because a datum alone
                      // does not: two wedstrijddagen may fall on one date.
                      const dag = {
                        datum: datum(plek.datum),
                        korps: plek.organiserendKorpsNaam
                          ? zonderVoorvoegsel(plek.organiserendKorpsNaam)
                          : t("lijst.nogNietBekend"),
                      };

                      return (
                        <li
                          key={`${plek.wedstrijddagId}-${plek.wedstrijdbaanId ?? ""}`}
                        >
                          {/* The dag alone where the reden never reached a baan: naming one
                              would point at a baan that is not what stopped the ploeg. */}
                          {plek.baanLabel
                            ? t("ongeplaatst.plekMetBaan", {
                                ...dag,
                                baan: plek.baanLabel,
                              })
                            : t("ongeplaatst.plek", dag)}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              {/* The cells that sentence is about. A beheerder reading "er is plaats" otherwise
                  has to walk the grid to find out which places, and then find out one by one
                  that each is ruled out. */}
              {ploeg.geblokkeerd.length > 0 && (
                <>
                  <p className="text-destructive-muted">
                    {t("ongeplaatst.geblokkeerdTitel")}
                  </p>
                  <ul className="list-disc pl-5 text-destructive-muted">
                    {ploeg.geblokkeerd.map((starttijd) => (
                      <li key={starttijd.speelschemaregelId}>
                        {t("ongeplaatst.geblokkeerd", {
                          datum: datum(starttijd.datum),
                          baan: starttijd.baanLabel,
                          tijd: tijd(starttijd.starttijd),
                          reden: starttijd.reden,
                        })}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
